/**
 * Eigenes Passwort aendern.
 *
 * Steht jedem angemeldeten Konto offen -- Administrator wie Viewer. Das ist
 * eine persoenliche Sicherheitsfunktion des Kontos, keine fachliche
 * Schreibberechtigung: der Viewer bleibt danach genauso strikt lesend.
 *
 * Bewusst keine Profilverwaltung. Ein Feld, das hier nicht steht, kann auch
 * nicht versehentlich aenderbar werden.
 */
import { useEffect, useRef, useState } from "react";
import { MIN_PASSWORD_LENGTH } from "@homebase/shared";
import { api } from "../lib/api.ts";
import { useI18n } from "../i18n/index.tsx";
import { errorText } from "../i18n/errors.ts";
import "./PasswordChange.css";

export function PasswordChange({ onClose, onChanged }: {
  onClose: () => void;
  onChanged: () => void;
}): React.JSX.Element {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [repeat, setRepeat] = useState("");
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const firstField = useRef<HTMLInputElement>(null);
  const i18n = useI18n();
  const t = i18n.t;

  useEffect(() => { firstField.current?.focus(); }, []);

  // Die Eingaben werden beim Verlassen ueberschrieben. Das ist keine echte
  // Schutzmassnahme -- die Zeichenketten bleiben bis zur Speicherbereinigung
  // erhalten -- aber es verhindert, dass ein spaeteres erneutes Oeffnen noch
  // etwas Eingetipptes zeigt.
  useEffect(() => () => { setCurrent(""); setNext(""); setRepeat(""); }, []);

  const tooShort = next.length > 0 && next.length < MIN_PASSWORD_LENGTH;
  const mismatch = repeat.length > 0 && next !== repeat;
  const ready = current.length > 0 && next.length >= MIN_PASSWORD_LENGTH && next === repeat && next !== current;

  async function submit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    if (!ready || busy) return;
    setBusy(true); setError(null);
    try {
      await api.changePassword({ currentPassword: current, newPassword: next, newPasswordRepeat: repeat });
      setCurrent(""); setNext(""); setRepeat("");
      onChanged();
    } catch (err) {
      setError(err);
    } finally { setBusy(false); }
  }

  return (
    <div className="pw__backdrop" onKeyDown={(e) => { if (e.key === "Escape") onClose(); }}>
      <div className="pw" role="dialog" aria-modal="true" aria-labelledby="pw-title">
        <h2 className="pw__title" id="pw-title">{t("pw.title")}</h2>
        <p className="pw__intro">{t("pw.intro")}</p>

        <form onSubmit={submit}>
          <div className="pw__field">
            <label htmlFor="pw-cur">{t("pw.current")}</label>
            <input id="pw-cur" ref={firstField} type="password" value={current}
              autoComplete="current-password" onChange={(e) => setCurrent(e.target.value)} />
          </div>
          <div className="pw__field">
            <label htmlFor="pw-new">{t("pw.new")}</label>
            <input id="pw-new" type="password" value={next}
              autoComplete="new-password" onChange={(e) => setNext(e.target.value)}
              aria-describedby="pw-rule" />
            <span className="pw__rule" id="pw-rule">{t("pw.rule", { n: MIN_PASSWORD_LENGTH })}</span>
            {tooShort && <span className="pw__hint">{t("pw.tooShort")}</span>}
          </div>
          <div className="pw__field">
            <label htmlFor="pw-rep">{t("pw.repeat")}</label>
            <input id="pw-rep" type="password" value={repeat}
              autoComplete="new-password" onChange={(e) => setRepeat(e.target.value)} />
            {mismatch && <span className="pw__hint">{t("pw.mismatch")}</span>}
          </div>

          {error !== null && <p className="pw__error" role="alert">{errorText(i18n, error)}</p>}

          <div className="pw__actions">
            <button type="submit" className="btn btn--primary" disabled={!ready || busy}>
              {busy ? t("pw.submitBusy") : t("pw.submit")}
            </button>
            <button type="button" className="btn" onClick={onClose}>{t("common.cancel")}</button>
          </div>
        </form>
      </div>
    </div>
  );
}
